var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps } from '@hawkins/helpers';
import { NavigationOrientationContext, NavigationTierContext, NavigationTransparencyContext, } from '../hooks/useNavigationContext';
import { TIER_2_HEIGHT_ANIMATION_DURATION, useHorizontalNavigationStyles, } from './horizontalNavigation.styles';
import { HorizontalNavigationTestIds } from './navigation.testids';
var displayName = 'HorizontalNavigation';
/**
 * Navigation enables users to orient themselves and move through an app.
 *
 * `HorizontalNavigation` is helpful when a user needs the entire content surface for their work.
 *
 * Hawkins’s navigation-related components (`NavigationMenuItem`, `NavigationQuickAction`, etc.)
 * will automatically style themselves based on whether they are in a `HorizontalNavigation` or a `VerticalNavigation`.
 *
 * If you want to build custom components for your navigation, you can use the `useNavigationContext` hook
 * to access the current navigation context (horizontal/vertical orientation, tier, etc.).
 */
export var HorizontalNavigation = React.forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _e = useProps(displayName, props), classesProp = _e.classes, className = _e.className, _f = _e.component, Component = _f === void 0 ? 'nav' : _f, _g = _e.fixed, fixed = _g === void 0 ? false : _g, layout = _e.layout, logo = _e.logo, quickActions = _e.quickActions, styleProp = _e.style, tieredContent = _e.tieredContent, _h = _e.transparent, transparent = _h === void 0 ? false : _h, userControls = _e.userControls, restProps = __rest(_e, ["classes", "className", "component", "fixed", "layout", "logo", "quickActions", "style", "tieredContent", "transparent", "userControls"]);
    var isTiered = tieredContent !== undefined;
    var isTier2Expanded = isTiered && tieredContent !== null;
    // State
    var _j = __read(React.useState(false), 2), showTier2Content = _j[0], setShowTier2Content = _j[1];
    React.useEffect(function () {
        if (isTier2Expanded) {
            var timeout_1 = setTimeout(function () {
                setShowTier2Content(true);
            }, TIER_2_HEIGHT_ANIMATION_DURATION);
            return function () {
                clearTimeout(timeout_1);
            };
        }
        setShowTier2Content(false);
    }, [isTier2Expanded]);
    // Styles
    var baseClasses = useHorizontalNavigationStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_b = {}, _b[classes.rootFixed] = fixed, _b));
    var tier1ClassName = clsx(classes.tier1, (_c = {},
        _c[classes.tier1Transparent] = transparent,
        _c[classes.tier1WithTier2] = isTier2Expanded,
        _c));
    var tier2ClassName = clsx(classes.tier2, (_d = {},
        _d[classes.tier2Expanded] = isTier2Expanded,
        _d[classes.tier2Transparent] = transparent,
        _d));
    return (_jsx(NavigationOrientationContext.Provider, __assign({ value: "horizontal" }, { children: _jsx(NavigationTransparencyContext.Provider, __assign({ value: transparent }, { children: _jsxs(Component, __assign({ className: rootClassName, "data-testid": HorizontalNavigationTestIds.ROOT, ref: ref, style: style }, restProps, { children: [_jsxs("div", __assign({ className: tier1ClassName }, { children: [_jsxs(NavigationTierContext.Provider, __assign({ value: 1 }, { children: [logo && _jsx("div", __assign({ className: classes.logoContainer }, { children: logo })), _jsxs("div", __assign({ className: classes.tier1MainContent }, { children: [_jsx("div", __assign({ className: classes.childrenContainer }, { children: children })), quickActions && _jsx("div", { children: quickActions }), userControls && _jsx("div", { children: userControls })] }))] })), _jsx(NavigationTierContext.Provider, __assign({ value: 2 }, { children: _jsx("div", __assign({ className: classes.tier2Content }, { children: showTier2Content && tieredContent })) }))] })), isTiered && (
                    // Note that this is a "fake" tier 2 for styling purposes.
                    // Tier 2 content is actually contained in the div above.
                    _jsx("div", { className: tier2ClassName }))] })) })) })));
});
HorizontalNavigation.displayName = displayName;
